/* eslint-disable react/jsx-sort-props */
/* eslint-disable react/jsx-max-props-per-line */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import { Box, Button, Chip, Stack, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { CustomFilterV2, TableV2 } from '../../../components/v2';
import { tableV2StackedCellContentSx } from '../../../components/v2/tableV2Styles';
import { renderStatus } from 'components/v2/cell-renderers/status';
import * as selectorsAuth from '../../../store/auth/reducer';
import * as selectorsModuleConfiguration from '../../../store/moduleConfiguration/reducer';
import * as actionsRequisicao from '../../../store/requisicao/actions';
import Formulario from './Formulario';
import ResumoCards from './ResumoCards';
import { canOperarAreaRequisicao, isRequisicaoAdmin } from './permissoes';
import { REQUISICAO_AREA_LABELS, REQUISICAO_AREA_OPTIONS } from './requisicaoArea';
import {
  getRequisicaoStatusLabel,
  isRequisicaoAguardando,
  isRequisicaoCriador,
  normalizeRequisicaoStatus,
} from './statusLabels';

const toOffsetStart = (ts) =>
  ts ? moment(Number(ts)).startOf('day').format('YYYY-MM-DDTHH:mm:ssZ') : null;

const toOffsetEnd = (ts) =>
  ts ? moment(Number(ts)).endOf('day').format('YYYY-MM-DDTHH:mm:ssZ') : null;

const QUERY_REQUISICAO_ID = 'requisicaoId';
const QUERY_MODO = 'modo';

const getRequisicaoIdFromSearch = (search) => {
  if (!search) return null;
  const params = new URLSearchParams(search);
  const raw = params.get(QUERY_REQUISICAO_ID) || params.get('id');
  return raw && String(raw).trim() ? String(raw).trim() : null;
};

const defaultFilter = () => ({
  inicio: null,
  fim: null,
  inspetorId: null,
  area: null,
});

const Requisicoes = ({
  authData,
  history,
  location,
  moduleConfigurations,
  deleteRequisicao,
}) => {
  const isAdmin = useMemo(
    () => isRequisicaoAdmin(moduleConfigurations, authData),
    [moduleConfigurations, authData]
  );

  const [open, setOpen] = useState(false);
  const [id, setId] = useState(null);
  const [editing, setEditing] = useState(false);
  const [localFilter, setLocalFilter] = useState(defaultFilter);
  const [activeStatus, setActiveStatus] = useState(null);
  const [reload, setReload] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);

  const apiFilterBase = useMemo(() => {
    const solicitanteId = Array.isArray(localFilter.inspetorId)
      ? localFilter.inspetorId[0] || null
      : localFilter.inspetorId || null;
    return {
      solicitanteId: isAdmin ? solicitanteId || null : null,
      dataInicio: toOffsetStart(localFilter.inicio),
      dataFim: toOffsetEnd(localFilter.fim),
      area: localFilter.area || null,
    };
  }, [isAdmin, localFilter]);

  const apiFilter = useMemo(
    () => ({
      ...apiFilterBase,
      status: !activeStatus
        ? null
        : activeStatus === 'AGUARDANDO'
          ? ['AGUARDANDO', 'ENVIADO', 'RASCUNHO']
          : [activeStatus],
    }),
    [apiFilterBase, activeStatus]
  );

  useEffect(() => {
    const params = new URLSearchParams(location?.search || '');
    const deepLinkId = getRequisicaoIdFromSearch(location?.search);
    if (!deepLinkId) return;
    if (deepLinkId === 'novo') {
      setEditing(false);
      setId(null);
      setOpen(true);
      return;
    }
    setEditing(params.get(QUERY_MODO) === 'editar');
    setId(deepLinkId);
    setOpen(true);
  }, [location?.search]);

  const replaceSearch = (mutate) => {
    const params = new URLSearchParams(location.search);
    mutate(params);
    const nextSearch = params.toString();
    history.replace({
      pathname: location.pathname,
      search: nextSearch ? `?${nextSearch}` : '',
    });
  };

  const setRequisicaoIdInUrl = (requisicaoId, edit) => {
    replaceSearch((params) => {
      params.delete('id');
      if (requisicaoId == null || requisicaoId === '') {
        params.delete(QUERY_REQUISICAO_ID);
        params.delete(QUERY_MODO);
        return;
      }
      params.set(QUERY_REQUISICAO_ID, String(requisicaoId));
      if (edit) params.set(QUERY_MODO, 'editar');
      else params.delete(QUERY_MODO);
    });
  };

  const openRequisicao = (rowId, edit) => {
    setEditing(!!edit);
    setOpen(true);
    setId(rowId);
    setRequisicaoIdInUrl(rowId, edit);
  };

  const onStartEdit = () => {
    if (!id) return;
    setEditing(true);
    setRequisicaoIdInUrl(id, true);
  };

  const onSaved = () => {
    setEditing(false);
    if (id) setRequisicaoIdInUrl(id, false);
    setReload((current) => !current);
  };

  const onExitEdit = () => {
    setEditing(false);
    if (id) setRequisicaoIdInUrl(id, false);
  };

  const onAdd = () => {
    setEditing(false);
    setOpen(true);
    setId(null);
    setRequisicaoIdInUrl('novo');
  };

  const clearDeepLinkId = () => {
    const params = new URLSearchParams(location.search);
    if (!params.get(QUERY_REQUISICAO_ID) && !params.get('id')) return;
    replaceSearch((next) => {
      next.delete(QUERY_REQUISICAO_ID);
      next.delete(QUERY_MODO);
      next.delete('id');
    });
  };

  const onClose = (isSave) => {
    setOpen(false);
    setId(null);
    setEditing(false);
    clearDeepLinkId();
    if (isSave) {
      setReload((current) => !current);
    }
  };

  const filterFields = [
    { field: 'useDates' },
    {
      field: 'useSelectOption',
      key: 'area',
      label: 'Área',
      md: 12,
      options: REQUISICAO_AREA_OPTIONS,
    },
    ...(isAdmin ? [{ field: 'useInspetor', md: 12 }] : []),
  ];

  return (
    <Box>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          sx={{ pr: 2 }}
        >
          <Typography variant="h4">Requisições</Typography>
          <Button
            variant="outlined"
            startIcon={<FilterListOutlinedIcon />}
            onClick={() => setOpenFilter(true)}
          >
            Filtros
          </Button>
        </Stack>
        <ResumoCards
          apiFilterBase={apiFilterBase}
          activeStatus={activeStatus}
          onFilterByStatus={setActiveStatus}
          refreshKey={reload}
        />
        <TableV2
          useApi
          fetchUrl="requisicoes/filtro"
          rowHeight={70}
          headerActions={[
            {
              label: 'Novo',
              onClick: onAdd,
              startIcon: <AddIcon />,
              variant: 'contained',
            },
          ]}
          columns={[
            {
              field: 'dataSolicitacao',
              headerName: 'Requisição',
              width: 170,
              renderCell: ({ row }) => (
                <Box sx={tableV2StackedCellContentSx}>
                  <Typography variant="body2">
                    {row.dataSolicitacao
                      ? moment(row.dataSolicitacao).format('DD/MM/YYYY')
                      : '—'}
                  </Typography>
                  <Typography variant="caption" component="div">
                    {row.numero ? `#${row.numero}` : '—'}
                  </Typography>
                </Box>
              ),
            },
            {
              field: 'area',
              headerName: 'Área',
              width: 140,
              sortable: false,
              renderCell: ({ row }) => {
                const label = REQUISICAO_AREA_LABELS[row.area];
                if (!label) return '—';
                return (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={label}
                    sx={{ height: 22, maxWidth: '100%' }}
                  />
                );
              },
            },
            {
              field: 'solicitanteNome',
              headerName: 'Solicitante',
              width: 180,
            },
            {
              field: 'descricao',
              headerName: 'Descrição',
              flex: 1,
              minWidth: 220,
              renderCell: ({ row }) => (
                <Box sx={tableV2StackedCellContentSx}>
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {row.descricao || '—'}
                  </Typography>
                  {row.dataNecessaria ? (
                    <Tooltip
                      title="Prazo desejado pelo solicitante para receber os itens"
                      enterDelay={400}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          color: 'text.secondary',
                          minWidth: 0,
                        }}
                      >
                        <EventAvailableOutlinedIcon sx={{ fontSize: 14 }} />
                        <Typography variant="caption" noWrap>
                          Prazo {moment(row.dataNecessaria).format('DD/MM/YYYY')}
                        </Typography>
                      </Box>
                    </Tooltip>
                  ) : null}
                </Box>
              ),
            },
            {
              field: 'quantidadeItens',
              headerName: 'Itens',
              width: 90,
              sortable: false,
              renderCell: ({ row }) => row.quantidadeItens ?? 0,
            },
            {
              field: 'status',
              headerName: 'Status',
              width: 180,
              renderCell: (params) =>
                renderStatus({
                  value: normalizeRequisicaoStatus(params.row.status),
                  label: getRequisicaoStatusLabel(params.row.status),
                }),
            },
          ]}
          actions={[
            {
              key: 'edit',
              icon: <EditIcon fontSize="small" />,
              title: 'Editar',
              show: (row) => isRequisicaoAguardando(row.status)
                && isRequisicaoCriador(row, authData?.id, authData?.nome),
              onClick: (rowId) => openRequisicao(rowId, true),
            },
            {
              key: 'view',
              icon: <VisibilityIcon fontSize="small" />,
              title: 'Visualizar',
              onClick: (rowId) => openRequisicao(rowId, false),
            },
          ]}
          onDelete={(rowId) => {
            deleteRequisicao(rowId, (ok) => {
              if (ok) setReload((current) => !current);
            });
          }}
          canDelete={(row) =>
            isRequisicaoAguardando(row.status)
            && (
              isRequisicaoCriador(row, authData?.id, authData?.nome)
              || canOperarAreaRequisicao(moduleConfigurations, authData, row.area)
            )
          }
          filterData={apiFilter}
          refreshKey={reload}
        />
        <CustomFilterV2
          variant="dialog"
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          title="Filtros"
          fullWidth
          appliedFilter={localFilter}
          defaultFilter={defaultFilter()}
          fields={filterFields}
          onFilter={(filterData) => {
            setLocalFilter(filterData);
            setOpenFilter(false);
          }}
        />
        <Formulario
          id={id}
          editing={editing}
          open={open}
          onClose={onClose}
          onStartEdit={onStartEdit}
          onSaved={onSaved}
          onExitEdit={onExitEdit}
        />
      </Stack>
    </Box>
  );
};

Requisicoes.propTypes = {
  authData: PropTypes.object,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  moduleConfigurations: PropTypes.array,
  deleteRequisicao: PropTypes.func.isRequired,
};

Requisicoes.defaultProps = {
  moduleConfigurations: [],
};

const mapStateToProps = (state) => ({
  authData: selectorsAuth.getAuth(state),
  moduleConfigurations: selectorsModuleConfiguration.getModuleConfigurations(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ deleteRequisicao: actionsRequisicao.deleteRequisicao }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Requisicoes));
